import { inject, Injectable } from '@angular/core';
import { PreloadingStrategy, Route, Router } from '@angular/router';
import { Observable, of, switchMap, timer } from 'rxjs';

const DEFAULT_DELAY_PRELOAD = 0;

/**
 * To add preloading of a component in the routing file, add 'data: { preload: true }' to the route where lazy loading is implemented.
 * The default delay time is 0 ms. To change it, add the 'delayPreload' field with the required delay time to 'data'. For example, 'data: { preload: true, delayPreload: 4000}'
 *
 * To conditionally preload based on current URL, add 'requiredPath' field to route data.
 * The component will only be preloaded if the current URL contains the specified path.
 * Example: 'data: { preload: true, requiredPath: PROJECT_DETAIL_ROUTES.SCHEDULES.path }'
 */

@Injectable({
	providedIn: 'root',
})
export class AppPreloadingStrategy implements PreloadingStrategy {
	private readonly router = inject(Router);

	public preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
		if (!this.shouldPreload(route)) {
			return of(null);
		}

		const delay = this.getPreloadDelay(route);
		return this.preloadWithDelay(load, delay);
	}

	private shouldPreload(route: Route): boolean {
		return route.data?.preload && this.isPathConditionMet(route);
	}

	private isPathConditionMet(route: Route): boolean {
		const requiredPath = route.data?.requiredPath;
		if (!requiredPath || typeof requiredPath !== 'string') {
			return true;
		}

		return this.router.url.includes(requiredPath);
	}

	private getPreloadDelay(route: Route): number {
		return route.data?.delayPreload ?? DEFAULT_DELAY_PRELOAD;
	}

	private preloadWithDelay(load: () => Observable<unknown>, delay: number): Observable<unknown> {
		return timer(delay).pipe(switchMap(() => load()));
	}
}
